import type { PublicUser, SetupStatus } from "@confluence/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { get, post } from "../api";
import { useI18n } from "../i18n";
import { useSetMe, useSetupStatus } from "../session";
import { Alert, AuthCard, Field, useForm } from "../ui";

const query = (name: string) => new URLSearchParams(window.location.search).get(name);

function useSignedIn() {
  const setMe = useSetMe();
  const navigate = useNavigate();
  const { setLocale } = useI18n();
  return (r: { user: PublicUser }) => {
    setMe(r.user);
    setLocale(r.user.locale);
    navigate({ to: "/" });
  };
}

export function LoginPage() {
  const { t } = useI18n();
  const signedIn = useSignedIn();
  const setup = useSetupStatus();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  // Double authentification : le mot de passe donne un « défi », puis on demande le code.
  const form = useForm(
    () => post<{ user?: PublicUser; totpRequired?: boolean; challenge?: string }>("/api/auth/login", { email, password, remember }),
    (r) => (r.totpRequired ? setChallenge(r.challenge!) : signedIn(r as { user: PublicUser })),
  );
  const second = useForm(() => post<{ user: PublicUser }>("/api/auth/login/totp", { challenge, code, remember }), signedIn);
  const canSignUp = setup.data?.registrationMode === "open";

  if (challenge) {
    return (
      <AuthCard title={t("totp.title")}>
        <form className="stack" onSubmit={second.onSubmit} noValidate>
          <p className="muted">{t("totp.loginBody")}</p>
          {second.error && <Alert>{second.error}</Alert>}
          <Field
            label={t("totp.code")}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint={t("totp.backupHint")}
          />
          <button className="btn btn-primary btn-block" disabled={second.pending || code.trim().length < 6}>
            {t("login.submit")}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setChallenge(null);
              setCode("");
            }}
          >
            {t("common.back")}
          </button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t("login.title")}
      footer={
        canSignUp && (
          <>
            {t("login.noAccount")} <Link to="/signup">{t("login.create")}</Link>
          </>
        )
      }
    >
      <form className="stack" onSubmit={form.onSubmit} noValidate>
        {form.error && <Alert>{form.error}</Alert>}
        <Field label={t("common.email")} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label={t("common.password")} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <label className="check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          {t("login.remember")}
        </label>
        <button className="btn btn-primary btn-block" disabled={form.pending}>
          {t("login.submit")}
        </button>
        <Link to="/forgot-password" className="link-muted">
          {t("login.forgot")}
        </Link>
      </form>
    </AuthCard>
  );
}

export function SignupPage() {
  const { t, locale } = useI18n();
  const signedIn = useSignedIn();
  const setup = useSetupStatus();
  const invite = useRef(query("invite")).current;
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { errorText } = useI18n();

  useEffect(() => {
    if (!invite) return;
    get<{ email: string }>(`/api/auth/invitation?token=${encodeURIComponent(invite)}`)
      .then((r) => {
        setInvitedEmail(r.email);
        setEmail(r.email);
      })
      .catch((err) => setInviteError(errorText(err)));
  }, [invite]);

  const form = useForm(
    () =>
      post<{ user?: PublicUser; verificationSent?: boolean }>("/api/auth/signup", {
        name,
        email,
        password,
        locale,
        ...(invite ? { invitationToken: invite } : {}),
      }),
    (r) => (r.user ? signedIn({ user: r.user }) : setSentTo(email)),
  );
  const resend = useForm(() => post("/api/auth/resend-verification", { email: sentTo }));
  const [resent, setResent] = useState(false);

  const footer = (
    <>
      {t("signup.haveAccount")} <Link to="/login">{t("login.submit")}</Link>
    </>
  );

  if (sentTo) {
    return (
      <AuthCard title={t("signup.checkTitle")} footer={footer}>
        <p>{t("signup.checkBody", { email: sentTo })}</p>
        {resent ? (
          <Alert kind="ok">{t("signup.resent")}</Alert>
        ) : (
          <form onSubmit={(e) => resend.onSubmit(e).then(() => setResent(true))}>
            <button className="btn">{t("signup.resend")}</button>
          </form>
        )}
      </AuthCard>
    );
  }

  const mode = (setup.data as SetupStatus | undefined)?.registrationMode;
  const blocked = !invite && (mode === "closed" || mode === "invite");

  return (
    <AuthCard title={t("signup.title")} footer={footer}>
      {blocked ? (
        <Alert kind="info">{mode === "closed" ? t("signup.closed") : t("signup.inviteOnly")}</Alert>
      ) : inviteError ? (
        <Alert>{inviteError}</Alert>
      ) : (
        <form className="stack" onSubmit={form.onSubmit} noValidate>
          {invitedEmail && <Alert kind="info">{t("signup.invited", { email: invitedEmail })}</Alert>}
          {form.error && <Alert>{form.error}</Alert>}
          <Field label={t("common.name")} autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} error={form.fields.name} />
          <Field label={t("common.email")} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} error={form.fields.email} />
          <Field
            label={t("common.password")}
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={t("common.passwordHint")}
            error={form.fields.password}
          />
          <button className="btn btn-primary btn-block" disabled={form.pending}>
            {t("signup.submit")}
          </button>
        </form>
      )}
    </AuthCard>
  );
}

export function VerifyEmailPage() {
  const { t, errorText } = useI18n();
  const signedIn = useSignedIn();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    post<{ user: PublicUser }>("/api/auth/verify-email", { token: query("token") ?? "" })
      .then(signedIn)
      .catch((err) => setError(errorText(err)));
  }, []);
  return (
    <AuthCard title={t("verify.title")} footer={<Link to="/login">{t("common.backToLogin")}</Link>}>
      {error ? <Alert>{error}</Alert> : <p className="muted">{t("verify.working")}</p>}
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const form = useForm(() => post("/api/auth/forgot-password", { email }), () => setSent(true));
  return (
    <AuthCard title={t("forgot.title")} footer={<Link to="/login">{t("common.backToLogin")}</Link>}>
      {sent ? (
        <Alert kind="ok">{t("forgot.sent")}</Alert>
      ) : (
        <form className="stack" onSubmit={form.onSubmit} noValidate>
          <p className="muted">{t("forgot.body")}</p>
          {form.error && <Alert>{form.error}</Alert>}
          <Field label={t("common.email")} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="btn btn-primary btn-block" disabled={form.pending}>
            {t("forgot.submit")}
          </button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const { t } = useI18n();
  const signedIn = useSignedIn();
  const [password, setPassword] = useState("");
  const form = useForm(
    () => post<{ user: PublicUser }>("/api/auth/reset-password", { token: query("token") ?? "", password }),
    signedIn,
  );
  return (
    <AuthCard title={t("reset.title")} footer={<Link to="/login">{t("common.backToLogin")}</Link>}>
      <form className="stack" onSubmit={form.onSubmit} noValidate>
        {form.error && <Alert>{form.error}</Alert>}
        <Field
          label={t("password.new")}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t("common.passwordHint")}
          error={form.fields.password}
        />
        <button className="btn btn-primary btn-block" disabled={form.pending}>
          {t("reset.submit")}
        </button>
      </form>
    </AuthCard>
  );
}
